import React, { useCallback, useEffect, useState } from 'react';
import { IVideoPlaylist } from './types';
import { IVideoPlaylistDetail } from '../../types';
import { GET_VIDEO_DATA_API } from '../../utils/constants';
import fetchApi from '../../../../utils/fetchApi';
import {
  FlexGrid,
  Heading,
  Responsive,
  Sizes,
  TextBoldness,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
  getClassNameFactory,
} from '@dcl/ui';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { DESKTOP_WIDTH, MOBILE_WIDTH } from './mock';
import { VideoTabList } from './components/VideoTabList/VideoTabList';
import { VideoTabListMobile } from './components/VideoTabList/VideoTabListMobile';
import { VideoPlayerPlaylist } from './components/PlaylistVideoPlayer/PlaylistVideoPlayer';
import ReactHtmlParser from 'react-html-parser';
import './VideoPlayerList.css';

export const VideoPlayerList: React.FC<IVideoPlaylist> = (
  props: IVideoPlaylist
) => {
  const { title, subTitle, ks, videoDetails } = props;
  const [playListUserData, setPlayListUserData] = useState<
    IVideoPlaylistDetail[]
  >(videoDetails);
  const [currentEntryId, setCurrentEntryId] = useState(
    videoDetails[0].entry_id
  );
  const userKeyKs = ks;

  const currentVideoHandler = (video: IVideoPlaylistDetail) => {
    setCurrentEntryId(video.entry_id);
  };

  const initiateVideoDataApi = useCallback(
    async (entryId) => {
      const videoResponse = await fetchApi({
        baseHostUrl: GET_VIDEO_DATA_API,
        options: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entryId: entryId,
            version: -1,
            ks: userKeyKs,
            format: 1,
          }),
        },
      });

      const videoMetaData = await videoResponse
        .text()
        .then((data) => JSON.parse(data));

      if (videoMetaData.objectType === 'KalturaMediaEntry') {
        let videoData = {
          videoTitle: videoMetaData.name,
          videoDescription: videoMetaData.description,
          videoViews: videoMetaData.views,
          videoDuration: videoMetaData.duration,
          userKeyKs,
        };
        return { videoData };
      } else {
        let videoData = {
          videoTitle: '',
          videoDescription: '',
          videoViews: '',
          videoDuration: '',
          userKeyKs,
        };
        return { videoData };
      }
    },
    [userKeyKs]
  );

  useEffect(() => {
    const updateUserData = async () => {
      const updatedUserData = await Promise.all(
        videoDetails.map(async (playList) => {
          const { videoData } = await initiateVideoDataApi(playList.entry_id);
          return { ...playList, videoData };
        })
      );
      setPlayListUserData(updatedUserData);
    };

    userKeyKs !== '' && updateUserData();
  }, [initiateVideoDataApi, videoDetails, userKeyKs]);
  const getClassName = getClassNameFactory(VideoPlayerList.displayName);
  return (
    <div className={getClassName()}>
      <div>
        {title && (
          <Heading
            type={types.Small}
            boldType={boldTypes.medium}
            data-testid="test-title-heading"
            className={getClassName({
              descendantName: 'title',
            })}
          >
            {title}
          </Heading>
        )}
        <VerticalSpacing size={VerticalSpacingSizes.S}>
          {subTitle && (
            <Typography
              type={Sizes.XXTRASMALL}
              tag={TypographyTags.P}
              boldness={TextBoldness.Medium}
              data-testid="test-subtitle"
              className={getClassName({
                descendantName: 'subTitle',
              })}
            >
              {ReactHtmlParser(subTitle)}
            </Typography>
          )}
        </VerticalSpacing>
      </div>
      <br />
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col
            md={3}
            lg={3}
            sm={12}
            className={getClassName({ descendantName: 'videoNavbar' })}
          >
            <Responsive minWidth={0} maxWidth={MOBILE_WIDTH}>
              <VideoTabListMobile
                playListUserData={playListUserData}
                currentVideoHandler={currentVideoHandler}
              />
            </Responsive>
            <Responsive minWidth={DESKTOP_WIDTH}>
              <VideoTabList
                playListUserData={playListUserData}
                currentVideoHandler={currentVideoHandler}
              />
            </Responsive>
          </FlexGrid.Col>
          <FlexGrid.Col md={9} lg={9} sm={12}>
            {playListUserData.map((playList) => {
              if (currentEntryId === playList.entry_id && playList.videoData)
                return (
                  <VideoPlayerPlaylist
                    userKeyKs={userKeyKs}
                    entryId={playList.entry_id}
                    key={playList.entry_id}
                    title={playList.videoData.videoTitle}
                    duration={playList.videoData.videoDuration}
                    views={playList.videoData.videoViews}
                    format={playList.videoFormat}
                  />
                );
              else return null;
            })}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};

VideoPlayerList.displayName = 'VideoPlayerList';
